// ── Historial ──────────────────────────────────────────────────────────────
import React, { useState, useMemo, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useEntries } from "../hooks/useEntries";
import { useInvestments } from "../hooks/useInvestments";
import { useHuchas } from "../hooks/useHuchas";
import { usePendientes } from "../hooks/usePendientes";
import { useClientes } from "../hooks/useClientes";
import { useBiometricLock } from "../hooks/useBiometricLock";
import { useNavigate, useSearchParams } from "react-router-dom";
import { t } from "../i18n";
import * as XLSX from "xlsx-js-style";
import { collection, getDocs, deleteDoc, doc } from "firebase/firestore";
import {
  EmailAuthProvider,
  reauthenticateWithCredential,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db, auth } from "../firebase";
import EditEntryModal from "./EditEntryModal";
import { CURRENCIES, getCurrencySymbol, formatAmount } from "../config/currencies";
import { getThemePalette } from "../config/themePalette";
import {
  TrendingDown, TrendingUp, Trash2, Search, Wallet, CircleCheck,
  Contact, AlertTriangle, Upload, ArrowUpDown, Pencil, Star, Crown, BarChart3,
  Target, PiggyBank, CreditCard, Calendar, Lock, Palette, Coffee,
  Monitor, FileText, FileDown, Mail, Archive, PartyPopper, Sun, ChevronUp, ChevronDown, Coins, Receipt, Settings,
  Hourglass, Infinity as InfinityIcon, History as HistoryIcon, Paperclip, Fingerprint,
} from "lucide-react";

const MONTHS = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];
const MONTHS_EN = ["January","February","March","April","May","June",
                   "July","August","September","October","November","December"];

// Iconos de la página Pro, uno por cada fila de pro_features en i18n.js
// (en el mismo orden): ilimitadas, historial, gráficos, cuentas, CSV,
// personalización, soporte.
const PRO_FEATURE_ICONS = [InfinityIcon, HistoryIcon, BarChart3, CreditCard, FileDown, Palette, Mail];

// Genera los datos (array de filas) de una hoja de Excel en "modo negocio":
// una fila por operación, en orden cronológico. Los datos de identificación
// (Cliente, Matrícula...) van justo al principio, y el dinero se resume en
// solo tres columnas siempre iguales — Ingreso, Gasto, Beneficio — en vez de
// un importe con signo más columnas repetidas para operaciones combinadas.
// Así una operación combinada se lee igual de simple que una normal: cuánto
// entró, cuánto salió, y el beneficio de esa operación.
// Campos personalizados: hasta 10. Las cuentas antiguas guardaron solo 3,
// así que se rellenan hasta 10 sin tocar los ya configurados.
const CUSTOM_FIELDS_MAX = 10;
function padCustomFields(arr) {
  const base = Array.isArray(arr) ? arr.slice(0, CUSTOM_FIELDS_MAX) : [];
  while (base.length < CUSTOM_FIELDS_MAX) base.push({ name: "", enabled: false });
  return base;
}

function buildBusinessSheetData(monthEntries, activeCustomFields, lang) {
  const hasCombined = monthEntries.some(e => e.isCombined);

  const header = [
    lang==="en" ? "Date and time" : "Fecha y hora",
    ...activeCustomFields.map(f => f.name),
    lang==="en" ? "Category"    : "Categoría",
    lang==="en" ? "Description" : "Descripción",
    lang==="en" ? "Account"     : "Cuenta",
    lang==="en" ? "Income"      : "Ingreso",
    lang==="en" ? "Expense"     : "Gasto",
  ];
  if (hasCombined) {
    header.push(lang==="en" ? "Expense description" : "Descripción del gasto");
  }
  header.push(
    lang==="en" ? "Profit"          : "Beneficio",
    lang==="en" ? "Running balance" : "Balance acumulado",
  );

  // Orden cronológico ascendente (de más antiguo a más reciente), para que
  // el balance acumulado tenga sentido leído de arriba a abajo.
  const parseDate = (dt) => {
    if (!dt) return 0;
    const [datePart, timePart] = dt.split(" ");
    const [d, m, y] = (datePart || "").split("/");
    const [h, min] = (timePart || "00:00").split(":");
    return new Date(y, (parseInt(m)||1)-1, d||1, h||0, min||0).getTime();
  };
  const sorted = [...monthEntries].sort((a,b) => parseDate(a.datetime) - parseDate(b.datetime));

  let running = 0, totalIncome = 0, totalExpense = 0;
  const rows = sorted.map(e => {
    // Ingreso y Gasto son SIEMPRE las dos mismas columnas, tanto para una
    // entrada normal (solo una de las dos tendrá valor) como para una
    // operación combinada (las dos tendrán valor). El beneficio es
    // simplemente Ingreso − Gasto de esa fila.
    const incomeVal  = e.isCombined ? (e.incomeAmount  || 0) : (e.type==="income"  ? e.amount : 0);
    const expenseVal = e.isCombined ? (e.expenseAmount || 0) : (e.type==="expense" ? e.amount : 0);
    const profit = incomeVal - expenseVal;
    running += profit;
    totalIncome  += incomeVal;
    totalExpense += expenseVal;

    const row = [
      e.datetime || `${e.month} ${e.year}`,
      ...activeCustomFields.map(f => e[f.key] || ""),
      e.cat || "General",
      e.desc || "",
      e.payment || "",
      incomeVal  || "",
      expenseVal || "",
    ];
    if (hasCombined) row.push(e.isCombined ? (e.expenseDesc || "") : "");
    row.push(profit, running);
    return row;
  });

  const pad = (n) => Array(n).fill("");
  const wsData = [header, ...rows, []];

  // Filas de totales: alineadas con las columnas de Ingreso/Gasto/Beneficio/Balance
  const leadingBlanks = pad(1 + activeCustomFields.length + 3); // fecha + custom + cat/desc/cuenta
  const totalsRow  = [...leadingBlanks, totalIncome, totalExpense, ...(hasCombined?[""]:[]), totalIncome-totalExpense, running];
  totalsRow[0] = lang==="en" ? "TOTALS" : "TOTALES";
  wsData.push(totalsRow);

  // Índices de columna (0-based) para poder colorear Categoría/Descripción/Ingreso/Gasto/Beneficio
  const catCol     = 1 + activeCustomFields.length;
  const descCol    = catCol + 1;
  const incomeCol  = catCol + 3;
  const expenseCol = incomeCol + 1;
  const profitCol  = expenseCol + 1 + (hasCombined ? 1 : 0);

  return { wsData, colCount: header.length, catCol, descCol, incomeCol, expenseCol, profitCol };
}

// Colorea en verde los importes de Ingreso/Beneficio positivo, y en rojo los
// de Gasto/Beneficio negativo, en una hoja generada por buildBusinessSheetData.
// También colorea la propia Categoría y Descripción: verde si la fila es de
// ingreso, rojo si es de gasto, y según el signo del beneficio si es una
// operación combinada (tiene ambas cosas a la vez).
// Requiere xlsx-js-style (el paquete "xlsx" normal no soporta estilos).
function styleBusinessSheet(ws, { catCol, descCol, incomeCol, expenseCol, profitCol }) {
  if (!ws["!ref"]) return;
  const GREEN = "1B7F3A";
  const RED = "C0392B";
  const range = XLSX.utils.decode_range(ws["!ref"]);

  // Cabecera en negrita con fondo gris claro
  for (let c = range.s.c; c <= range.e.c; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) ws[addr].s = { font: { bold: true }, fill: { fgColor: { rgb: "EFEFEF" } } };
  }

  for (let r = 1; r <= range.e.r; r++) {
    const catAddr     = XLSX.utils.encode_cell({ r, c: catCol });
    const descAddr    = XLSX.utils.encode_cell({ r, c: descCol });
    const incomeAddr  = XLSX.utils.encode_cell({ r, c: incomeCol });
    const expenseAddr = XLSX.utils.encode_cell({ r, c: expenseCol });
    const profitAddr  = XLSX.utils.encode_cell({ r, c: profitCol });

    const hasIncome  = ws[incomeAddr]  && ws[incomeAddr].v  !== "" && ws[incomeAddr].v  !== undefined;
    const hasExpense = ws[expenseAddr] && ws[expenseAddr].v !== "" && ws[expenseAddr].v !== undefined;

    if (hasIncome) {
      ws[incomeAddr].s = { font: { color: { rgb: GREEN }, bold: true } };
    }
    if (hasExpense) {
      ws[expenseAddr].s = { font: { color: { rgb: RED }, bold: true } };
    }
    if (ws[profitAddr] && typeof ws[profitAddr].v === "number") {
      ws[profitAddr].s = { font: { color: { rgb: ws[profitAddr].v >= 0 ? GREEN : RED }, bold: true } };
    }

    // Categoría/Descripción: verde si solo hay ingreso, rojo si solo hay
    // gasto, y según el signo del beneficio si tiene las dos cosas (operación combinada)
    let rowColor = null;
    if (hasIncome && !hasExpense) rowColor = GREEN;
    else if (hasExpense && !hasIncome) rowColor = RED;
    else if (hasIncome && hasExpense && ws[profitAddr]) rowColor = ws[profitAddr].v >= 0 ? GREEN : RED;

    if (rowColor) {
      if (ws[catAddr] && ws[catAddr].v !== "" && ws[catAddr].v !== undefined) {
        ws[catAddr].s = { font: { color: { rgb: rowColor }, bold: true } };
      }
      if (ws[descAddr] && ws[descAddr].v !== "" && ws[descAddr].v !== undefined) {
        ws[descAddr].s = { font: { color: { rgb: rowColor } } };
      }
    }
  }
}

// Igual que arriba pero para el formato clásico COMPRAS/VENTAS en columnas
// paralelas (modo personal): colorea el texto y el VALOR de COMPRAS en rojo,
// los de VENTAS en verde, y el BENEFICIO de la fila de totales según su signo.
function stylePersonalSheet(ws) {
  if (!ws["!ref"]) return;
  const GREEN = "1B7F3A";
  const RED = "C0392B";
  const range = XLSX.utils.decode_range(ws["!ref"]);
  const compraTxtCol = 1, compraValCol = 2, ventaTxtCol = 4, ventaValCol = 5, beneficioCol = 6;

  for (let c = range.s.c; c <= range.e.c; c++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    if (ws[addr]) ws[addr].s = { font: { bold: true }, fill: { fgColor: { rgb: "EFEFEF" } } };
  }

  for (let r = 1; r <= range.e.r; r++) {
    const compraTxtAddr = XLSX.utils.encode_cell({ r, c: compraTxtCol });
    const compraAddr    = XLSX.utils.encode_cell({ r, c: compraValCol });
    const ventaTxtAddr  = XLSX.utils.encode_cell({ r, c: ventaTxtCol });
    const ventaAddr     = XLSX.utils.encode_cell({ r, c: ventaValCol });
    const beneficioAddr = XLSX.utils.encode_cell({ r, c: beneficioCol });
    if (ws[compraTxtAddr] && ws[compraTxtAddr].v !== "" && ws[compraTxtAddr].v !== undefined) {
      ws[compraTxtAddr].s = { font: { color: { rgb: RED }, bold: true } };
    }
    if (ws[compraAddr] && ws[compraAddr].v !== "" && ws[compraAddr].v !== undefined) {
      ws[compraAddr].s = { font: { color: { rgb: RED }, bold: true } };
    }
    if (ws[ventaTxtAddr] && ws[ventaTxtAddr].v !== "" && ws[ventaTxtAddr].v !== undefined) {
      ws[ventaTxtAddr].s = { font: { color: { rgb: GREEN }, bold: true } };
    }
    if (ws[ventaAddr] && ws[ventaAddr].v !== "" && ws[ventaAddr].v !== undefined) {
      ws[ventaAddr].s = { font: { color: { rgb: GREEN }, bold: true } };
    }
    if (ws[beneficioAddr] && ws[beneficioAddr].v !== "" && !isNaN(parseFloat(ws[beneficioAddr].v))) {
      const val = parseFloat(ws[beneficioAddr].v);
      ws[beneficioAddr].s = { font: { color: { rgb: val >= 0 ? GREEN : RED }, bold: true } };
    }
  }
}

export function Historial() {
  const { profile, lang, currency } = useAuth();
  const { visibleEntries, loading, deleteEntry, updateEntry, isPro } = useEntries();
  const { deleteInvestmentByEntryId } = useInvestments();
  const { deleteHuchaMovementByEntryId } = useHuchas();
  const { addPendiente } = usePendientes();
  const { clientes } = useClientes();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const c = profile?.config || {};
  const T = (key) => t(lang, key);
  const MONTH_LIST = lang === "en" ? MONTHS_EN : MONTHS;
  const fmt = n => formatAmount(n, currency);

  // Si llegamos desde un gráfico (Panel o Gráficos), aplicamos el filtro
  // que venga en la URL: ?year=2026&month=Marzo&type=expense&cat=Alquiler
  // Por defecto se muestra solo el mes en curso — si vienes de un enlace
  // con ?year=&month= (por ejemplo desde un gráfico del Panel), se respeta
  // ese; si no, y si quieres ver otro mes o "Todos", se cambia con el
  // propio buscador de abajo.
  const nowH = new Date();
  const [fYear,  setFYear]  = useState(searchParams.get("year")  || String(nowH.getFullYear()));
  const [fMonth, setFMonth] = useState(searchParams.get("month") || MONTHS[nowH.getMonth()]);
  const [fType,  setFType]  = useState(searchParams.get("type")  || "Todos");
  const [fSearch,setFSearch]= useState(searchParams.get("cat")   || "");
  // "Buscar en": "all" (todo), "desc" (categoría/descripción) o la clave de un campo personalizado (custom1..custom10)
  const [fSearchIn,setFSearchIn]= useState("all");
  const [fMin,   setFMin]   = useState("");
  const [fMax,   setFMax]   = useState("");
  const [expanded, setExpanded] = useState(null);

  // Al escribir algo en el buscador (p.ej. el nombre de un cliente), no
  // tiene sentido seguir limitado al mes/año seleccionado arriba —
  // normalmente el mes en curso, por defecto — porque entonces se pierden
  // sin avisar los movimientos de ese mismo cliente/categoría de otros
  // meses. En cuanto hay texto en el buscador, se amplían año y mes a
  // "Todos" para poder ver y agrupar el historial completo de lo buscado;
  // al borrar la búsqueda, se puede volver a acotar por mes/año a mano.
  useEffect(() => {
    if (fSearch.trim()) {
      setFYear(y => y === "Todos" ? y : "Todos");
      setFMonth(m => m === "Todos" ? m : "Todos");
    }
  }, [fSearch]);
  // Editar una entrada abre el mismo formulario de Añadir Entrada en una
  // ventana superpuesta (EditEntryModal), ya relleno con sus datos — así
  // la lista de aquí abajo no se mueve ni un pixel mientras se edita.
  const [editingEntry, setEditingEntry] = useState(null);

  // IMPORTANTE: React Router no vuelve a montar este componente cuando ya
  // estabas en /historial y navegas otra vez a /historial con parámetros
  // distintos (ej. desde Cuentas al pulsar otra cuenta) — solo cambia la
  // URL. Como los useState de arriba solo leen la URL una vez, al montar,
  // sin este efecto el filtro se quedaría "pegado" al primer valor y
  // navegaciones posteriores no lo actualizarían. Este efecto vuelve a
  // aplicar los parámetros de la URL cada vez que cambian de verdad.
  useEffect(() => {
    setFYear(searchParams.get("year") || String(nowH.getFullYear()));
    setFMonth(searchParams.get("month") || MONTHS[nowH.getMonth()]);
    setFType(searchParams.get("type") || "Todos");
    setFSearch(searchParams.get("cat") || "");
  }, [searchParams]);

  // Si llegamos desde el Panel al clicar una entrada concreta
  // (?entryId=xxx), la expandimos automáticamente y le hacemos scroll en
  // cuanto esté disponible en la lista filtrada.
  const entryIdParam = searchParams.get("entryId");
  const scrolledToEntryRef = useRef(false);

  const years = useMemo(()=>[...new Set(visibleEntries.map(e=>e.year))].sort(),[visibleEntries]);

  // Campos personalizados: solo en modo negocio, y solo los activados/nombrados.
  // Se calcula aquí (antes del buscador) para poder incluir sus valores en la
  // búsqueda por texto de más abajo.
  const isNegocio = profile?.perfil === "negocio";
  const activeCustomFields = isNegocio
    ? (c.custom_fields || [])
        .map((f, i) => ({ ...f, key: `custom${i + 1}` }))
        .filter(f => f.enabled && f.name && f.name.trim())
    : [];

  // Mapa id → contacto, para poder buscar también por sus datos (nombre,
  // teléfono, DNI, email) desde una entrada que solo guarda "clienteId" —
  // sin esto, buscar el teléfono de un contacto no encontraba nada, aunque
  // la entrada estuviera vinculada a él.
  const clientesById = useMemo(
    () => Object.fromEntries(clientes.map(cl => [cl.id, cl])),
    [clientes]
  );

  const filtered = useMemo(()=>{
    const mn = fMin ? parseFloat(fMin) : null;
    const mx = fMax ? parseFloat(fMax) : null;
    const q  = fSearch.toLowerCase().trim();
    return visibleEntries.filter(e=>{
      if (fYear!=="Todos" && e.year!==parseInt(fYear)) return false;
      if (fMonth!=="Todos" && e.month!==fMonth) return false;
      if (fType==="income"  && e.type!=="income")  return false;
      if (fType==="expense" && e.type!=="expense") return false;
      if (q) {
        // Búsqueda por texto: incluye cat/desc/mes/cuenta y también el
        // valor de cada campo personalizado activo (ej. Cliente, Matrícula,
        // Mesa), para que buscar por esos datos encuentre la entrada.
        const customVals = activeCustomFields.map(f => e[f.key] || "").join(" ");
        // Búsqueda acotada a un solo campo ("Buscar en: Matrícula", etc.)
        if (fSearchIn !== "all") {
          const target = fSearchIn === "desc"
            ? `${e.cat||""} ${e.desc||""}`
            : String(e[fSearchIn] || "");
          if (!target.toLowerCase().includes(q)) return false;
        } else {
        // Si la entrada tiene un contacto vinculado (clienteId), se busca
        // también por sus datos — nombre, teléfono, DNI, email — aunque
        // esos datos no estén guardados en la propia entrada.
        const contacto = e.clienteId ? clientesById[e.clienteId] : null;
        const contactoVals = contacto
          ? `${contacto.nombre||""} ${contacto.telefono||""} ${contacto.dni||""} ${contacto.email||""}`
          : "";
        const haystack = `${e.cat||""} ${e.desc||""} ${e.month} ${e.payment||""} ${customVals} ${contactoVals}`.toLowerCase();
        if (!haystack.includes(q)) return false;
        }
      }
      if (mn!==null && e.amount<mn) return false;
      if (mx!==null && e.amount>mx) return false;
      return true;
    }).sort((a,b)=>{
      const da = new Date(a.year, MONTHS.indexOf(a.month), 1);
      const db2= new Date(b.year, MONTHS.indexOf(b.month), 1);
      return db2-da;
    });
  },[visibleEntries,fYear,fMonth,fType,fSearch,fSearchIn,fMin,fMax,activeCustomFields,clientesById]);

  // Agrupa la lista ya filtrada por mes y año para poder mostrarla con
  // cabeceras — antes se mostraba como una sola lista plana, así que al
  // buscar un cliente que aparece en varios meses (ver el efecto de
  // arriba, que ahora amplía la búsqueda a "Todos") todo se mezclaba sin
  // distinguir de qué mes era cada uno.
  const groupedFiltered = useMemo(() => {
    const groups = [];
    let current = null;
    filtered.forEach(e => {
      const key = `${e.year}-${e.month}`;
      if (!current || current.key !== key) {
        current = { key, year: e.year, month: e.month, entries: [] };
        groups.push(current);
      }
      current.entries.push(e);
    });
    return groups;
  }, [filtered]);

  useEffect(() => {
    if (!entryIdParam || scrolledToEntryRef.current || loading) return;
    const exists = filtered.some(e => e.id === entryIdParam);
    if (!exists) {
      // El enlace que trae aquí (p.ej. desde el Panel) no siempre indica
      // año/mes, así que por defecto se buscaba solo dentro del mes en
      // curso — si el movimiento era de otro mes, no aparecía nunca y el
      // usuario se quedaba viendo el mes actual sin más explicación. Si el
      // movimiento SÍ existe pero está fuera del filtro de mes/año actual,
      // se amplía a "Todos" para que pueda encontrarse y mostrarse.
      const target = visibleEntries.find(e => e.id === entryIdParam);
      if (target && (fYear !== "Todos" || fMonth !== "Todos")) {
        setFYear("Todos");
        setFMonth("Todos");
      }
      return;
    }
    setExpanded(entryIdParam);
    scrolledToEntryRef.current = true;
    // Pequeño margen para que la tarjeta ya esté pintada en el DOM antes de hacer scroll
    setTimeout(() => {
      const el = document.getElementById(`entry-${entryIdParam}`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 150);
  }, [entryIdParam, filtered, loading, visibleEntries, fYear, fMonth]);

  // En una operación combinada (ingreso y gasto en un solo movimiento,
  // p.ej. cobro 185 - comisión 30 = +155 neto), "type"/"amount" solo
  // guardan el NETO — sumar solo por "type" dejaba el lado del gasto de
  // esas operaciones fuera del total de gastos por completo. Aquí se usan
  // sus dos importes por separado (incomeAmount/expenseAmount) para que
  // el gasto de una combinada sí se sume al total de gastos, y su ingreso
  // al total de ingresos, en vez de solo su neto.
  const tv = filtered.reduce((s,e) => s + (e.isCombined ? (e.incomeAmount||0)  : (e.type==="income"  ? e.amount : 0)), 0);
  const tc = filtered.reduce((s,e) => s + (e.isCombined ? (e.expenseAmount||0) : (e.type==="expense" ? e.amount : 0)), 0);

  function exportExcel() {
    const wb = XLSX.utils.book_new();

    if (isNegocio) {
      // Modo negocio: una hoja por mes, una fila por operación, orden
      // cronológico, balance acumulado y columnas propias por cada campo
      // personalizado (Cliente, Matrícula, Mesa...).
      const byMonth = {};
      filtered.forEach(e => {
        const key = `${e.year}-${e.month}`;
        if (!byMonth[key]) byMonth[key] = { year: e.year, month: e.month, entries: [] };
        byMonth[key].entries.push(e);
      });
      const sortedKeys = Object.keys(byMonth).sort((a,b) => {
        const [ay,am] = a.split("-"); const [by2,bm] = b.split("-");
        return ay!==by2 ? ay-by2 : MONTHS.indexOf(am)-MONTHS.indexOf(bm);
      });
      sortedKeys.forEach(key => {
        const { year, month, entries: monthEntries } = byMonth[key];
        const { wsData, colCount, catCol, descCol, incomeCol, expenseCol, profitCol } = buildBusinessSheetData(monthEntries, activeCustomFields, lang);
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws["!cols"] = Array(colCount).fill({wch:18});
        styleBusinessSheet(ws, { catCol, descCol, incomeCol, expenseCol, profitCol });
        XLSX.utils.book_append_sheet(wb, ws, `${month}_${year}`.slice(0,31));
      });
    } else {
      // Modo personal: formato clásico COMPRAS/VENTAS en columnas paralelas
      const byMonth = {};
      filtered.forEach(e => {
        const key = `${e.year}-${e.month}`;
        if (!byMonth[key]) byMonth[key] = { year: e.year, month: e.month, income: [], expense: [] };
        if (e.type==="income")  byMonth[key].income.push(e);
        if (e.type==="expense") byMonth[key].expense.push(e);
      });
      const sortedKeys = Object.keys(byMonth).sort((a,b) => {
        const [ay,am] = a.split("-"); const [by2,bm] = b.split("-");
        return ay!==by2 ? ay-by2 : MONTHS.indexOf(am)-MONTHS.indexOf(bm);
      });
      sortedKeys.forEach(key => {
        const { year, month, income, expense } = byMonth[key];
        const maxRows = Math.max(income.length, expense.length);
        const wsData = [
          ["FECHA", "COMPRAS", "VALOR", "", "VENTAS", "VALOR", "BENEFICIO"],
        ];
        for (let i = 0; i < maxRows; i++) {
          const inc = income[i];
          const exp = expense[i];
          const fechaExp = exp?.datetime || `01/${String(MONTHS.indexOf(month)+1).padStart(2,"0")}/${year}`;
          const fechaInc = inc?.datetime || `01/${String(MONTHS.indexOf(month)+1).padStart(2,"0")}/${year}`;
          const fecha = exp ? fechaExp : fechaInc;
          wsData.push([
            fecha,
            exp ? (exp.desc || exp.cat || "") : "",
            exp ? exp.amount : "",
            "",
            inc ? (inc.desc || inc.cat || "") : "",
            inc ? inc.amount : "",
            "",
          ]);
        }
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws["!cols"] = [{wch:14},{wch:28},{wch:8},{wch:4},{wch:28},{wch:8},{wch:10}];
        stylePersonalSheet(ws);
        XLSX.utils.book_append_sheet(wb, ws, `${month}_${year}`.slice(0,31));
      });
    }

    const periodo = fMonth!=="Todos" ? `${fMonth}_${fYear}` : fYear;
    XLSX.writeFile(wb, `historial_${periodo}.xlsx`);
  }

  // Editar ya no monta un formulario aparte aquí dentro — simplemente abre
  // EditEntryModal con la entrada completa; toda la lógica de guardado
  // (incluida la conversión a pendiente, combinada, etc.) vive ahí, igual
  // que en Añadir Entrada.
  function startEdit(e, ev) {
    ev.stopPropagation();
    setEditingEntry(e);
  }

  if (loading) return <div className="page"><p style={{color:"var(--gray-400)"}}>Cargando...</p></div>;

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{display:"flex",alignItems:"center",gap:8}}><Search size={22}/> {T("hist_title")}</h2>
          <p className="page-sub">
            {filtered.length} {T("hist_results")} · 
            <span style={{color:"var(--green)",fontWeight:600,display:"inline-flex",alignItems:"center",gap:3}}> <TrendingUp size={12}/> {fmt(tv)}</span> ·
            <span style={{color:"var(--red)",fontWeight:600,display:"inline-flex",alignItems:"center",gap:3}}> <TrendingDown size={12}/> {fmt(tc)}</span> ·
            <span style={{color:tv-tc>=0?"var(--green)":"var(--red)",fontWeight:600}}> = {fmt(tv-tc)}</span>
          </p>
        </div>
        {isPro && filtered.length > 0 && (
          <button className="btn btn-outline" onClick={exportExcel} style={{display:"flex",alignItems:"center",gap:6}}
            title={lang==="en"
              ? "Exports the year/month selected in the filters below (or everything, if set to \"All\")"
              : "Exporta el año/mes seleccionado en los filtros de abajo (o todo, si están en \"Todos\")"}>
            <FileDown size={15}/> {lang==="en"?"Export Excel":"Exportar Excel"}
          </button>
        )}
      </div>

      {!isPro && (
        <div className="alert alert-info" style={{marginBottom:16}}>
          {T("hist_free_plan")} <button style={{background:"none",border:"none",color:"var(--blue)",cursor:"pointer",fontWeight:600}} onClick={()=>navigate("/pro")}>{T("hist_see_all")}</button>
        </div>
      )}

      <div className="filters">
        <select className="filter-select" value={fYear} onChange={e=>setFYear(e.target.value)}>
          <option value="Todos">{T("hist_all_years")}</option>
          {years.map(y=><option key={y}>{y}</option>)}
        </select>
        <select className="filter-select" value={fMonth} onChange={e=>setFMonth(e.target.value)}>
          <option value="Todos">{T("hist_all_months")}</option>
          {MONTHS.map((m,i)=><option key={m} value={m}>{MONTH_LIST[i]}</option>)}
        </select>
        <select className="filter-select" value={fType} onChange={e=>setFType(e.target.value)}>
          <option value="Todos">{T("hist_all_types")}</option>
          <option value="income">{c.income_label||T("income_label")}</option>
          <option value="expense">{c.expense_label||T("expense_label")}</option>
        </select>
        {activeCustomFields.length > 0 && (
          <select className="filter-select" value={fSearchIn} onChange={e=>setFSearchIn(e.target.value)}
                  aria-label={lang==="en" ? "Search in" : "Buscar en"}>
            <option value="all">{lang==="en" ? "Search in: everything" : "Buscar en: todo"}</option>
            <option value="desc">{lang==="en" ? "Description" : "Descripción"}</option>
            {activeCustomFields.map(f => <option key={f.key} value={f.key}>{f.name}</option>)}
          </select>
        )}
        <input className="filter-select"
               placeholder={activeCustomFields.length > 0
                 ? `${T("hist_search")} (${activeCustomFields.map(f=>f.name).join(", ")}...)`
                 : T("hist_search")}
               value={fSearch} onChange={e=>setFSearch(e.target.value)} style={{minWidth:160}}/>
        <input className="filter-select" placeholder={T("hist_min")} value={fMin} onChange={e=>setFMin(e.target.value)} style={{width:80}}/>
        <input className="filter-select" placeholder={T("hist_max")} value={fMax} onChange={e=>setFMax(e.target.value)} style={{width:80}}/>
      </div>

      {(fSearch || fType!=="Todos" || fMin || fMax) && filtered.length > 0 && (
        <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:12}}>
          {tv > 0 && (
            <div style={{background:"var(--green-l,#f0fdf4)",border:"1px solid var(--green)",borderRadius:"var(--radius-sm)",
                         padding:"8px 14px",fontSize:13,fontWeight:600,color:"var(--green)",display:"flex",alignItems:"center",gap:5}}>
              <TrendingUp size={13}/> {fmt(tv)}
            </div>
          )}
          {tc > 0 && (
            <div style={{background:"var(--red-l)",border:"1px solid var(--red)",borderRadius:"var(--radius-sm)",
                         padding:"8px 14px",fontSize:13,fontWeight:600,color:"var(--red)",display:"flex",alignItems:"center",gap:5}}>
              <TrendingDown size={13}/> {fmt(tc)}
            </div>
          )}
          {tv > 0 && tc > 0 && (
            <div style={{background:"var(--gray-50)",border:"1px solid var(--border)",borderRadius:"var(--radius-sm)",
                         padding:"8px 14px",fontSize:13,fontWeight:600,color:(tv-tc)>=0?"var(--green)":"var(--red)"}}>
              = {fmt(tv-tc)}
            </div>
          )}
        </div>
      )}

      {filtered.length===0 ? (
        <div className="empty"><div className="empty-icon"><Search size={26}/></div><h3>{T("hist_no_results")}</h3></div>
      ) : (
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {groupedFiltered.map(group => (
          <div key={group.key}>
            {/* Cabecera de mes/año — sobre todo útil al buscar, donde ahora
                se ven todos los meses a la vez en vez de solo el actual */}
            {groupedFiltered.length > 1 && (
              <div style={{fontSize:12,fontWeight:700,color:"var(--gray-400)",textTransform:"uppercase",
                           letterSpacing:.3,margin:"14px 2px 6px"}}>
                {MONTH_LIST[MONTHS.indexOf(group.month)]} {group.year}
              </div>
            )}
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
          {group.entries.map(e=>{
            const isOpen = expanded === e.id;
            const isLinked = e.id === entryIdParam;
            return (
              <div key={e.id} id={`entry-${e.id}`}
                   style={{background:"var(--card)",borderRadius:"var(--radius)",
                           border:isLinked ? "2px solid var(--blue)" : "1px solid var(--border)",
                           boxShadow:isLinked ? "0 0 0 3px var(--blue-l,rgba(59,130,246,.15))" : "0 1px 4px rgba(0,0,0,.05)",
                           overflow:"hidden",cursor:"pointer"}}
                   onClick={()=>setExpanded(isOpen ? null : e.id)}>
                {/* Cabecera */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 14px"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,flex:1,minWidth:0}}>
                    <span style={{flexShrink:0,display:"inline-flex",alignItems:"center",
                                  color:e.isCombined?"var(--blue)":(e.type==="income"?"var(--green)":"var(--red)")}}>
                      {e.isCombined ? <ArrowUpDown size={15}/> : (e.type==="income"?<TrendingUp size={15}/>:<TrendingDown size={15}/>)}
                    </span>
                    <div style={{minWidth:0}}>
                      <div style={{fontSize:13,fontWeight:600,color:"var(--gray-800)",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                        {e.cat||"General"}{e.desc ? ` · ${e.desc}` : ""}
                        {e.isCombined && <span style={{marginLeft:6,fontSize:11,fontWeight:500,color:"var(--gray-400)"}}>
                          ({lang==="en"?"combined":"combinada"})
                        </span>}
                      </div>
                      <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2,display:"flex",alignItems:"center",gap:4,flexWrap:"wrap"}}>
                        <span>{e.datetime||`${e.month} ${e.year}`}</span>
                        {e.clienteId && clientesById[e.clienteId] && (
                          <span style={{display:"inline-flex",alignItems:"center",gap:3}}>· <Contact size={11}/> {clientesById[e.clienteId].nombre}</span>
                        )}
                        {e.receiptPath && (
                          <span style={{display:"inline-flex",alignItems:"center",gap:3,cursor:"pointer",color:"var(--blue)"}}
                                onClick={async (ev)=>{
                                  ev.stopPropagation();
                                  try {
                                    const { getAttachmentUrl } = await import("./receiptOcr");
                                    const url = await getAttachmentUrl(e.receiptPath);
                                    window.open(url, "_blank");
                                  } catch (err) {
                                    console.error("No se pudo abrir el ticket:", err);
                                    alert(lang==="en" ? "Couldn't open the receipt" : "No se pudo abrir el ticket");
                                  }
                                }}>
                            · <Paperclip size={11}/> {lang==="en" ? "Receipt" : "Ticket"}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
                    <span style={{fontWeight:700,fontSize:15,color:e.type==="income"?"var(--green)":"var(--red)"}}>
                      {e.type==="income"?"+":"-"}{fmt(e.amount)}
                    </span>
                    <span style={{color:"var(--gray-400)",transition:"transform .2s",display:"inline-flex",
                                  transform:isOpen?"rotate(180deg)":"rotate(0deg)"}}><ChevronDown size={15}/></span>
                  </div>
                </div>

                {/* Detalle expandible */}
                {isOpen && (
                  <div style={{borderTop:"1px solid var(--border)",padding:"12px 14px",background:"var(--gray-50)",
                               display:"flex",flexDirection:"column",gap:6}}
                       onClick={ev=>ev.stopPropagation()}>
                    {e.payment && (
                      <div style={{fontSize:13,color:"var(--gray-600)"}}>
                        <strong>{T("hist_account")}:</strong> {e.payment}
                      </div>
                    )}
                    {e.desc && (
                      <div style={{fontSize:13,color:"var(--gray-600)"}}>
                        <strong>{T("hist_desc")}:</strong> {e.desc}
                      </div>
                    )}
                    {e.clienteId && clientesById[e.clienteId] && (
                      <div style={{fontSize:13,color:"var(--gray-600)",display:"flex",alignItems:"center",gap:5}}>
                        <strong style={{display:"flex",alignItems:"center",gap:4}}><Contact size={13}/> {lang==="en"?"Contact":"Contacto"}:</strong> {clientesById[e.clienteId].nombre}
                        {clientesById[e.clienteId].telefono && ` · ${clientesById[e.clienteId].telefono}`}
                      </div>
                    )}
                    <div style={{fontSize:13,color:"var(--gray-600)"}}>
                      <strong>{T("hist_cat")}:</strong> {e.cat||"General"}
                    </div>
                    <div style={{fontSize:13,color:"var(--gray-600)"}}>
                      <strong>{T("hist_date")}:</strong> {e.datetime||`${e.month} ${e.year}`}
                    </div>
                    {e.isCombined && (
                      <div style={{marginTop:4,padding:"8px 10px",
                                   background:"white",borderRadius:"var(--radius-sm)",border:"1px solid var(--border)"}}>
                        <div style={{display:"flex",gap:14,flexWrap:"wrap"}}>
                          <div style={{fontSize:13,color:"var(--green)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
                            <CircleCheck size={13}/> {lang==="en"?"Income":"Ingreso"}: {fmt(e.incomeAmount||0)}
                          </div>
                          <div style={{fontSize:13,color:"var(--red)",fontWeight:600,display:"flex",alignItems:"center",gap:4}}>
                            <TrendingDown size={13}/> {lang==="en"?"Expense":"Gasto"}: {fmt(e.expenseAmount||0)}
                          </div>
                          <div style={{fontSize:13,fontWeight:700,color:e.type==="income"?"var(--green)":"var(--red)"}}>
                            = {lang==="en"?"Net":"Neto"}: {e.type==="income"?"+":"-"}{fmt(e.amount)}
                          </div>
                        </div>
                        {e.expenseDesc && (
                          <div style={{fontSize:12,color:"var(--gray-500)",marginTop:6,display:"flex",alignItems:"center",gap:4}}>
                            <TrendingDown size={11}/> {lang==="en"?"Expense description":"Descripción del gasto"}: {e.expenseDesc}
                          </div>
                        )}
                      </div>
                    )}
                    {activeCustomFields.filter(f => e[f.key]).map(f => (
                      <div key={f.key} style={{fontSize:13,color:"var(--gray-600)"}}>
                        <strong>{f.name}:</strong> {e[f.key]}
                      </div>
                    ))}
                    <div style={{display:"flex",gap:8,marginTop:4}}>
                      <button className="btn btn-outline btn-sm" style={{display:"flex",alignItems:"center",gap:5}}
                        onClick={ev=>startEdit(e, ev)}>
                        <Pencil size={13}/> {lang==="en"?"Edit":"Editar"}
                      </button>
                      <button className="btn btn-danger btn-sm" style={{display:"flex",alignItems:"center",gap:5}}
                        onClick={ev=>{
                          ev.stopPropagation();
                          if(window.confirm(T("hist_delete_confirm"))) {
                            // Si esta entrada vino de una aportación/retiro de
                            // Inversiones, hay que borrar también ese registro
                            // — si no, el dinero se queda "fantasma" reflejado
                            // ahí aunque el movimiento real ya no exista.
                            if (e.isInvestmentContribution || e.isInvestmentWithdrawal) {
                              deleteInvestmentByEntryId(e.id);
                            }
                            if (e.isHuchaMovement) {
                              deleteHuchaMovementByEntryId(e.id);
                            }
                            if (e.receiptPath) {
                              import("./receiptOcr").then(({ deleteAttachment }) => deleteAttachment(e.receiptPath));
                            }
                            deleteEntry(e.id);
                          }
                        }}>
                        <Trash2 size={13}/> {T("hist_delete_confirm").split("?")[0]}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
            </div>
          </div>
          ))}
        </div>
      )}

      {editingEntry && (
        <EditEntryModal entry={editingEntry} onClose={()=>setEditingEntry(null)} />
      )}
    </div>
  );
}

// ── Cuentas ────────────────────────────────────────────────────────────────
export function Cuentas() {
  const { profile, updateConfig, lang, currency, theme } = useAuth();
  const { visibleEntries, loading } = useEntries();
  const c = profile?.config || {};
  const accounts = c.accounts || [];
  const T = (key) => t(lang, key);
  const fmt = n => formatAmount(n, currency);

  // Paleta de colores cálidos/pastel filtrada según el tema activo (en vez
  // de colores fijos sin relación con el tema), para distinguir cuentas
  // visualmente entre sí.
  const PALETTE = useMemo(() => getThemePalette(theme), [theme]);

  // Campos personalizados activos (modo negocio), para mostrarlos en el
  // detalle expandido de cada movimiento si tienen valor.
  const isNegocioAcc = profile?.perfil === "negocio";
  const activeCustomFields = isNegocioAcc
    ? (c.custom_fields || [])
        .map((f, i) => ({ ...f, key: `custom${i + 1}` }))
        .filter(f => f.enabled && f.name && f.name.trim())
    : [];

  const [sel, setSel]   = useState(null);
  const [name, setName] = useState("");
  const [init, setInit] = useState("0.00");
  const [color,setColor]= useState(PALETTE[0]);
  const [err,  setErr]  = useState("");
  const [movSearch, setMovSearch] = useState("");
  const [expandedMovId, setExpandedMovId] = useState(null);

  // Buscador de periodo para el histórico de la cuenta seleccionada — por
  // defecto el mes en curso (igual que en Inversiones), con opción de ver
  // otro mes, un año completo, o "Todos" para el histórico de siempre.
  const now = new Date();
  const [fYear,  setFYear]  = useState(String(now.getFullYear()));
  const [fMonth, setFMonth] = useState(MONTHS[now.getMonth()]);

  async function addAccount() {
    if (!name.trim()) { setErr(T("acc_err_name")); return; }
    if (accounts.find(a=>a.name===name.trim())) { setErr(T("acc_err_exists")); return; }
    const initial = parseFloat(init)||0;
    const newAccs = [...accounts, {name:name.trim(), initial, color}];
    const newPay  = [...(c.payment_methods||[]), name.trim()];
    await updateConfig({...c, accounts:newAccs, payment_methods:newPay});
    setName(""); setInit("0.00"); setErr("");
  }

  async function deleteAccount(n) {
    if (!window.confirm(`${T("acc_delete_confirm")} "${n}"?`)) return;
    await updateConfig({...c,
      accounts: accounts.filter(a=>a.name!==n),
      payment_methods: (c.payment_methods||[]).filter(m=>m!==n)
    });
    if (sel===n) setSel(null);
  }

  const selAcc = accounts.find(a=>a.name===sel);
  const allSelMovs = sel ? visibleEntries.filter(e=>e.payment===sel).sort((a,b)=>{
    const da=new Date(a.year,MONTHS.indexOf(a.month),1);
    const db2=new Date(b.year,MONTHS.indexOf(b.month),1);
    return da-db2;
  }) : [];
  // Saldo acumulado REAL en cada movimiento, calculado sobre TODO el
  // histórico en orden cronológico — así, aunque luego se filtre la tabla
  // por mes/año, la columna de saldo sigue mostrando el saldo real de la
  // cuenta en ese momento (no un saldo "reiniciado" que no tendría sentido).
  const allSelMovsWithRunning = allSelMovs.reduce((acc,e) => {
    const prev = acc.length>0 ? acc[acc.length-1].running : (selAcc?.initial||0);
    const running = e.type==="income" ? prev+e.amount : prev-e.amount;
    acc.push({...e, running});
    return acc;
  }, []);
  // Saldo REAL de la cuenta: siempre de todo el histórico, nunca se
  // resetea (perder el rastro del dinero real no tendría sentido).
  const selIncAllTime = allSelMovs.filter(e=>e.type==="income").reduce((s,e)=>s+e.amount,0);
  const selExpAllTime = allSelMovs.filter(e=>e.type==="expense").reduce((s,e)=>s+e.amount,0);
  const selBalAllTime = (selAcc?.initial||0)+selIncAllTime-selExpAllTime;

  // Vista filtrada por periodo (mes/año concretos, o "Todos"): esta es la
  // que "se reinicia" cada mes — por defecto solo el mes en curso.
  const hasSpecificMonthFilter = fYear !== "Todos" && fMonth !== "Todos";
  const isAllTime = fYear === "Todos" && fMonth === "Todos";
  const selMovsByPeriod = allSelMovsWithRunning.filter(e =>
    (fYear === "Todos"  || e.year === parseInt(fYear)) &&
    (fMonth === "Todos" || e.month === fMonth)
  );
  // Buscador de texto libre dentro del periodo elegido — filtra por
  // categoría, descripción y campos personalizados si los hay.
  const selMovs = movSearch.trim()
    ? selMovsByPeriod.filter(e => {
        const q = movSearch.trim().toLowerCase();
        const haystack = `${e.cat||""} ${e.desc||""}`.toLowerCase();
        return haystack.includes(q);
      })
    : selMovsByPeriod;
  const selInc = selMovs.filter(e=>e.type==="income").reduce((s,e)=>s+e.amount,0);
  const selExp = selMovs.filter(e=>e.type==="expense").reduce((s,e)=>s+e.amount,0);
  const selNet = selInc - selExp;
  const periodLabel = isAllTime
    ? (lang === "en" ? "All time" : "Todo el histórico")
    : hasSpecificMonthFilter
      ? `${fMonth} ${fYear}`
      : fYear !== "Todos"
        ? `${lang === "en" ? "Year" : "Año"} ${fYear}`
        : `${fMonth} (${lang === "en" ? "all years" : "todos los años"})`;

  // Años disponibles para el desplegable
  const accountYears = [...new Set(allSelMovs.map(e => e.year))].sort((a,b)=>b-a);


  if (loading) return <div className="page"><p>Cargando...</p></div>;

  return (
    <div className="page">
      <h2 className="page-title" style={{marginBottom:20,display:"flex",alignItems:"center",gap:8}}><CreditCard size={22}/> {T("acc_title")}</h2>

      <div style={{display:"flex",flexDirection:"column",gap:10,marginBottom:16,maxWidth:640}}>
        {accounts.length===0 && <p style={{color:"var(--gray-400)",fontSize:13}}>{T("acc_no_accounts")}</p>}
        {accounts.map(acc=>{
          const inc=visibleEntries.filter(e=>e.type==="income"&&e.payment===acc.name).reduce((s,e)=>s+e.amount,0);
          const exp=visibleEntries.filter(e=>e.type==="expense"&&e.payment===acc.name).reduce((s,e)=>s+e.amount,0);
          const bal=(acc.initial||0)+inc-exp;
          const isSel=sel===acc.name;
          return (
            <div key={acc.name}>
              <div onClick={()=>{ setSel(isSel?null:acc.name); setMovSearch(""); setExpandedMovId(null); }}
                   style={{padding:"12px 14px",borderRadius:"var(--radius)",background:isSel?"white":"var(--gray-50)",
                           border:`2px solid ${isSel?acc.color:"var(--border)"}`,cursor:"pointer",transition:"all .15s",
                           borderBottomLeftRadius:isSel?0:"var(--radius)",borderBottomRightRadius:isSel?0:"var(--radius)"}}>
                <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:4}}>
                  <span style={{color:acc.color,fontSize:18}}>●</span>
                  <strong style={{fontSize:14}}>{acc.name}</strong>
                  <button onClick={e=>{e.stopPropagation();deleteAccount(acc.name);}}
                          style={{marginLeft:"auto",background:"none",border:"none",cursor:"pointer",color:"var(--gray-400)",display:"flex"}}><Trash2 size={14}/></button>
                  <span style={{fontSize:12,color:"var(--gray-400)",display:"inline-flex"}}>{isSel?<ChevronUp size={14}/>:<ChevronDown size={14}/>}</span>
                </div>
                <div style={{fontSize:20,fontWeight:700,color:bal>=0?"var(--green)":"var(--red)"}}>{fmt(bal)}</div>
                <div style={{fontSize:11,color:"var(--gray-400)",marginTop:2,display:"flex",alignItems:"center",gap:6}}>
                  <span style={{display:"inline-flex",alignItems:"center",gap:2}}><TrendingUp size={11}/>{fmt(inc)}</span>
                  <span style={{display:"inline-flex",alignItems:"center",gap:2}}><TrendingDown size={11}/>{fmt(exp)}</span>
                  <span>· {T("acc_initial_bal")}: {fmt(acc.initial||0)}</span>
                </div>
              </div>

              {/* Detalle desplegado justo debajo de la cuenta pulsada — tipo
                  acordeón, para no tener que bajar a otra sección de la página. */}
              {isSel && (
                <div style={{border:`2px solid ${acc.color}`,borderTop:"none",borderBottomLeftRadius:"var(--radius)",
                             borderBottomRightRadius:"var(--radius)",background:"white",padding:"16px 14px"}}>

                  {/* Saldo real de la cuenta: siempre de todo el histórico */}
                  <div className="metric-grid" style={{marginBottom:16}}>
                    {[[T("acc_initial_bal"),fmt(selAcc?.initial||0),"var(--gray-600)"],
                      [lang==="en"?"Current balance (all time)":"Saldo actual (de siempre)",fmt(selBalAllTime),selBalAllTime>=0?"var(--green)":"var(--red)"],
                    ].map(([l,v,col])=>(
                      <div className="metric-card" key={l}>
                        <div className="metric-label">{l}</div>
                        <div className="metric-value" style={{color:col,fontSize:18}}>{v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Buscador de periodo — por defecto el mes en curso */}
                  <div className="filters" style={{marginBottom:12}}>
                    <select className="filter-select" value={fYear} onChange={e=>setFYear(e.target.value)}>
                      <option value="Todos">{lang==="en"?"All years":"Todos los años"}</option>
                      {accountYears.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <select className="filter-select" value={fMonth} onChange={e=>setFMonth(e.target.value)}>
                      <option value="Todos">{lang==="en"?"All months":"Todos los meses"}</option>
                      {(lang==="en"?MONTHS_EN:MONTHS).map((m,i) => (
                        <option key={m} value={MONTHS[i]}>{m}</option>
                      ))}
                    </select>
                  </div>

                  <p style={{fontSize:12,fontWeight:600,color:"var(--gray-500)",marginBottom:10,textTransform:"uppercase",letterSpacing:".04em"}}>
                    {periodLabel}
                  </p>
                  <div className="metric-grid" style={{marginBottom:20}}>
                    {[[T("acc_total_in"),fmt(selInc),"var(--green)"],
                      [T("acc_total_out"),fmt(selExp),"var(--red)"],
                      [lang==="en"?"Net this period":"Neto de este periodo",fmt(selNet),selNet>=0?"var(--green)":"var(--red)"],
                    ].map(([l,v,col])=>(
                      <div className="metric-card" key={l}>
                        <div className="metric-label">{l}</div>
                        <div className="metric-value" style={{color:col,fontSize:18}}>{v}</div>
                      </div>
                    ))}
                  </div>

                  {/* Buscador dentro de esta cuenta */}
                  <div className="form-group" style={{marginBottom:12}} onClick={ev=>ev.stopPropagation()}>
                    <input className="form-input" value={movSearch} onChange={e=>setMovSearch(e.target.value)}
                           placeholder={lang==="en" ? "Search category, description..." : "Buscar categoría, descripción..."} />
                  </div>

                  {/* Total del resultado de la búsqueda — mismo estilo de píldoras que Pendientes/Historial */}
                  {movSearch.trim() && selMovs.length > 0 && (
                    <div style={{display:"flex",gap:10,marginBottom:12,flexWrap:"wrap"}} onClick={ev=>ev.stopPropagation()}>
                      {selInc > 0 && (
                        <div style={{background:"var(--green-l,rgba(27,127,58,.1))",borderRadius:20,
                                     padding:"8px 14px",fontSize:13,fontWeight:600,color:"var(--green)"}}>
                          {T("acc_total_in")}: {fmt(selInc)}
                        </div>
                      )}
                      {selExp > 0 && (
                        <div style={{background:"var(--red-l,rgba(192,57,43,.1))",borderRadius:20,
                                     padding:"8px 14px",fontSize:13,fontWeight:600,color:"var(--red)"}}>
                          {T("acc_total_out")}: {fmt(selExp)}
                        </div>
                      )}
                      {selInc > 0 && selExp > 0 && (
                        <div style={{background:"var(--gray-100)",borderRadius:20,
                                     padding:"8px 14px",fontSize:13,fontWeight:600,
                                     color:selNet>=0?"var(--green)":"var(--red)"}}>
                          = {fmt(selNet)}
                        </div>
                      )}
                    </div>
                  )}

                  {selMovs.length===0 ? (
                    <p style={{color:"var(--gray-400)",fontSize:13,textAlign:"center",padding:"30px 0"}}>{T("acc_no_movements")}</p>
                  ) : (
                    <div style={{display:"flex",flexDirection:"column",gap:8}} onClick={ev=>ev.stopPropagation()}>
                      {[...selMovs].reverse().map(e=>{
                        const isMovExpanded = expandedMovId === e.id;
                        return (
                        <div key={e.id} style={{background:"var(--card)",borderRadius:"var(--radius)",
                                                 border:"1px solid var(--border)",boxShadow:"0 1px 4px rgba(0,0,0,.05)",
                                                 padding:"11px 14px",cursor:"pointer"}}
                             onClick={()=>setExpandedMovId(isMovExpanded?null:e.id)}>
                          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",gap:10}}>
                            <div style={{display:"flex",alignItems:"center",gap:10,minWidth:0}}>
                              <span style={{flexShrink:0,display:"flex"}}>{e.type==="income"?<Wallet size={16}/>:<TrendingDown size={16}/>}</span>
                              <div style={{minWidth:0}}>
                                <div style={{fontSize:13,fontWeight:600,color:"var(--gray-800)",
                                             whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
                                  {e.cat||"General"}{e.desc?` · ${e.desc}`:""}
                                </div>
                                <div style={{fontSize:11,color:"var(--gray-400)"}}>{e.datetime||`${e.month} ${e.year}`}</div>
                              </div>
                            </div>
                            <div style={{textAlign:"right",flexShrink:0}}>
                              <div style={{fontWeight:700,fontSize:14,color:e.type==="income"?"var(--green)":"var(--red)"}}>
                                {e.type==="income"?"+":"-"}{fmt(e.amount)}
                              </div>
                              <div style={{fontSize:10,color:"var(--gray-400)"}}>
                                {T("acc_balance")}: <span style={{color:e.running>=0?"var(--green)":"var(--red)",fontWeight:600}}>{fmt(e.running)}</span>
                              </div>
                            </div>
                          </div>
                          {isMovExpanded && (
                            <div style={{marginTop:12,paddingTop:12,borderTop:"1px solid var(--border-soft,var(--border))",
                                         display:"flex",flexDirection:"column",gap:6,fontSize:13}}>
                              <div style={{display:"flex",justifyContent:"space-between"}}>
                                <span style={{color:"var(--gray-500)"}}>{lang==="en"?"Type":"Tipo"}</span>
                                <b>{e.type==="income"?(lang==="en"?"Income":"Ingreso"):(lang==="en"?"Expense":"Gasto")}</b>
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between"}}>
                                <span style={{color:"var(--gray-500)"}}>{lang==="en"?"Category":"Categoría"}</span>
                                <b>{e.cat||"—"}</b>
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between"}}>
                                <span style={{color:"var(--gray-500)"}}>{lang==="en"?"Description":"Descripción"}</span>
                                <b>{e.desc||"—"}</b>
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between"}}>
                                <span style={{color:"var(--gray-500)"}}>{lang==="en"?"Account":"Cuenta"}</span>
                                <b>{e.payment||"—"}</b>
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between"}}>
                                <span style={{color:"var(--gray-500)"}}>{lang==="en"?"Date":"Fecha"}</span>
                                <b>{e.datetime||`${e.month} ${e.year}`}</b>
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between"}}>
                                <span style={{color:"var(--gray-500)"}}>{lang==="en"?"Amount":"Importe"}</span>
                                <b style={{color:e.type==="income"?"var(--green)":"var(--red)"}}>
                                  {e.type==="income"?"+":"-"}{fmt(e.amount)}
                                </b>
                              </div>
                              <div style={{display:"flex",justifyContent:"space-between"}}>
                                <span style={{color:"var(--gray-500)"}}>{T("acc_balance")}</span>
                                <b style={{color:e.running>=0?"var(--green)":"var(--red)"}}>{fmt(e.running)}</b>
                              </div>
                              {activeCustomFields.filter(f=>e[f.key]).map(f=>(
                                <div key={f.key} style={{display:"flex",justifyContent:"space-between"}}>
                                  <span style={{color:"var(--gray-500)"}}>{f.name}</span>
                                  <b>{e[f.key]}</b>
                                </div>
                              ))}
                              <a href={`/historial?entryId=${e.id}`}
                                 style={{marginTop:6,fontSize:12,fontWeight:600,color:"var(--blue,#4f46e5)",textDecoration:"none"}}
                                 onClick={ev=>ev.stopPropagation()}>
                                {lang==="en" ? "Edit in History →" : "Editar en Historial →"}
                              </a>
                            </div>
                          )}
                        </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="card card-sm" style={{maxWidth:640}}>
        <p style={{fontSize:13,fontWeight:600,marginBottom:10}}>{T("acc_new")}</p>
        {err && <div className="alert alert-error" style={{marginBottom:8,padding:"8px 10px",fontSize:12}}>{err}</div>}
        <div className="form-group">
          <label className="form-label">{T("acc_name")}</label>
          <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder={T("acc_name_placeholder")} />
        </div>
        <div className="form-group">
          <label className="form-label">{T("acc_initial")}</label>
          <input className="form-input" type="number" step="0.01" value={init} onChange={e=>setInit(e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">{T("acc_color")}</label>
          <div style={{display:"flex",gap:6,flexWrap:"wrap"}}>
            {PALETTE.map(col=>(
              <div key={col} onClick={()=>setColor(col)}
                   style={{width:24,height:24,borderRadius:"50%",background:col,cursor:"pointer",
                           border:`3px solid ${color===col?"var(--gray-800)":"transparent"}`,transition:"border .1s"}}/>
            ))}
          </div>
        </div>
        <button className="btn btn-primary" style={{width:"100%",justifyContent:"center"}} onClick={addAccount}>
          {T("acc_add_btn")}
        </button>
      </div>
    </div>
  );
}

// ── Pro page ────────────────────────────────────────────────────────────────
export function ProPage() {
  const { user, effectivePlan, lang } = useAuth();
  const T = (key) => t(lang, key);
  const isPro = effectivePlan === "pro";

  return (
    <div className="page">
      <div style={{maxWidth:560,margin:"0 auto"}}>
        <div style={{textAlign:"center",padding:"32px 0 24px"}}>
          <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}><Star size={44}/></div>
          <h2 style={{fontSize:28,fontWeight:800,color:"var(--gray-900)",marginBottom:8}}>{T("pro_title")}</h2>
          <p style={{fontSize:16,color:"var(--gray-500)"}}>{T("pro_sub")}</p>
        </div>

        <div style={{background:"linear-gradient(135deg,#4f46e5,#7c3aed)",borderRadius:16,padding:"28px 32px",color:"white",marginBottom:24,textAlign:"center"}}>
          <div style={{fontSize:42,fontWeight:800,marginBottom:4}}>{T("pro_price")}<span style={{fontSize:18,fontWeight:400}}>{T("pro_month")}</span></div>
          <p style={{opacity:.8,fontSize:14}}>{T("pro_cancel")}</p>
        </div>

        <div className="card" style={{marginBottom:20}}>
          {T("pro_features").map(([,title,desc],i)=>{
            const Icon = PRO_FEATURE_ICONS[i] || Star;
            return (
              <div key={title} style={{display:"flex",gap:14,padding:"12px 0",borderBottom:"1px solid var(--border)"}}>
                <span style={{color:"var(--blue)",display:"flex",alignItems:"flex-start",flexShrink:0}}><Icon size={20}/></span>
                <div>
                  <p style={{fontWeight:600,fontSize:14,color:"var(--gray-800)"}}>{title}</p>
                  <p style={{fontSize:13,color:"var(--gray-500)"}}>{desc}</p>
                </div>
              </div>
            );
          })}
        </div>

        {isPro ? (
          <div className="alert alert-success" style={{textAlign:"center",fontSize:16,fontWeight:600}}>
            {T("pro_active")}
          </div>
        ) : (
          <div style={{textAlign:"center"}}>
            <button className="btn btn-pro" style={{width:"100%",justifyContent:"center",padding:"14px",fontSize:16,borderRadius:12}}
              onClick={async ()=>{ try { const res = await fetch("https://createcheckoutsession-7m6tnviebq-uc.a.run.app", { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify({userId: user.uid, email: user.email}) }); const data = await res.json(); if (data.url) window.location.href = data.url; } catch(e) { alert(T("pro_err")); } }}>
              {T("pro_btn")}
            </button>
            <p style={{textAlign:"center",fontSize:12,color:"var(--gray-400)",marginTop:10}}>{T("pro_note2")}</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Ajustes ─────────────────────────────────────────────────────────────────
export function Ajustes() {
  const { user, profile, updateConfig, logout, lang, updateLang, currency, updateCurrency, theme, updateTheme } = useAuth();
  const navigate = useNavigate();
  const c = profile?.config || {};
  const T = (key) => t(lang, key);
  const bioLock = useBiometricLock(user?.uid, user?.email);
  const [bioBusy, setBioBusy] = useState(false);

  async function handleToggleBioLock() {
    if (bioLock.enabled) { bioLock.disable(); return; }
    setBioBusy(true);
    const res = await bioLock.enable();
    setBioBusy(false);
    if (res.error && res.error !== "cancelled") {
      alert(lang==="en"
        ? "Couldn't set up Face ID / fingerprint on this device."
        : "No se pudo activar Face ID / huella en este dispositivo.");
    }
  }

  // Estado del plan / cancelación de suscripción
  const isPro = profile?.plan === "pro";
  const subStatus = profile?.subscriptionStatus; // "active" | "trialing" | "canceling" | "canceled" | undefined
  const subEndsAt = profile?.subscriptionEndsAt;
  const [cancelConfirm, setCancelConfirm] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelError, setCancelError] = useState("");

  async function handleCancelSubscription() {
    setCanceling(true);
    setCancelError("");
    try {
      const functions = getFunctions();
      const cancelFn = httpsCallable(functions, "cancelSubscription");
      await cancelFn();
      setCancelConfirm(false);
    } catch (err) {
      setCancelError(err?.message || (lang==="en"?"Could not cancel subscription.":"No se pudo cancelar la suscripción."));
    }
    setCanceling(false);
  }

  const [cfg, setCfg] = useState({
    income_label:  c.income_label  || "Ingresos",
    expense_label: c.expense_label || "Gastos",
    balance_label: c.balance_label || "Balance",
    fund_label:    c.fund_label    || "Ahorro",
    budget_label:  c.budget_label  || "Presupuestos",
    fund_pct:      c.fund_pct !== undefined ? c.fund_pct : 0,
    income_cats:   (c.income_cats  || []).join("\n"),
    expense_cats:  (c.expense_cats || []).join("\n"),
    panel_sections: c.panel_sections || {
      grafico: true,
      balance: true,
      presupuestos: true,
      cuentas: true,
      historial: true,
      huchas: true,
    },
    custom_fields: padCustomFields(c.custom_fields),
  });
  const [saved, setSaved] = useState(false);

  // BUGFIX: profile.config llega de Firestore de forma asíncrona (puede
  // tardar un instante tras crear la cuenta, o al cambiar de workspace).
  // El useState de arriba solo lee "c" en el primer render — si en ese
  // momento la config real todavía no había llegado, "cfg" se quedaba
  // congelado para siempre con income_cats/expense_cats vacíos, y
  // cualquier guardado posterior (cambiar fund_pct, o lo que fuera) los
  // sobrescribía con [] en Firestore, borrando las categorías reales.
  // Este efecto sincroniza "cfg" en cuanto la config real esté disponible,
  // pero solo la PRIMERA vez que detecta datos (configSyncedRef), para no
  // pisar cambios que el usuario ya esté escribiendo en el formulario.
  const configSyncedRef = useRef(false);
  useEffect(() => {
    if (configSyncedRef.current) return;
    if (!c || Object.keys(c).length === 0) return; // config aún no ha llegado
    configSyncedRef.current = true;
    setCfg({
      income_label:  c.income_label  || "Ingresos",
      expense_label: c.expense_label || "Gastos",
      balance_label: c.balance_label || "Balance",
      fund_label:    c.fund_label    || "Ahorro",
      budget_label:  c.budget_label  || "Presupuestos",
      fund_pct:      c.fund_pct !== undefined ? c.fund_pct : 0,
      income_cats:   (c.income_cats  || []).join("\n"),
      expense_cats:  (c.expense_cats || []).join("\n"),
      panel_sections: c.panel_sections || {
        grafico: true,
        balance: true,
        presupuestos: true,
        cuentas: true,
        historial: true,
        huchas: true,
      },
      custom_fields: padCustomFields(c.custom_fields),
    });
  }, [c]);


  async function handleSave(e) {
    e.preventDefault();
    await updateConfig({
      ...c,
      income_label:  cfg.income_label,
      expense_label: cfg.expense_label,
      balance_label: cfg.balance_label,
      fund_label:    cfg.fund_label,
      budget_label:  cfg.budget_label,
      fund_pct:      cfg.fund_pct === "" || cfg.fund_pct === null ? 0 : parseFloat(cfg.fund_pct),
      income_cats:   cfg.income_cats.split("\n").map(s=>s.trim()).filter(Boolean),
      expense_cats:  cfg.expense_cats.split("\n").map(s=>s.trim()).filter(Boolean),
      panel_sections: cfg.panel_sections,
      custom_fields: cfg.custom_fields,
    });
    setSaved(true); setTimeout(()=>setSaved(false),3000);
  }

  const expenseCats = cfg.expense_cats.split("\n").map(s=>s.trim()).filter(Boolean);

  return (
    <div className="page">
      <h2 className="page-title" style={{marginBottom:20,display:"flex",alignItems:"center",gap:8}}><Settings size={22}/> {T("settings_title")}</h2>
      {saved && <div className="alert alert-success" style={{maxWidth:560}}>{T("settings_saved")}</div>}

      {/* Plan y suscripción */}
      <div className="card" style={{marginBottom:16,maxWidth:560}}>
        <h3 style={{fontSize:15,fontWeight:600,marginBottom:12}}>
          <Star size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:5}}/> {lang==="en"?"Plan":"Plan"}
        </h3>
        {!isPro && (
          <p style={{fontSize:14,color:"var(--gray-600)"}}>
            {lang==="en"?"You're on the Free plan.":"Tienes el plan Gratuito."}
          </p>
        )}
        {isPro && subStatus !== "canceling" && (
          <>
            <p style={{fontSize:14,color:"var(--gray-700)",marginBottom:12,display:"flex",alignItems:"center",gap:6}}>
              <CircleCheck size={15}/> {lang==="en"?"You're on the Pro plan.":"Tienes el plan Pro activo."}
            </p>
            {!cancelConfirm ? (
              <button type="button" className="btn btn-outline btn-sm" onClick={()=>setCancelConfirm(true)}>
                {lang==="en"?"Cancel subscription":"Cancelar suscripción"}
              </button>
            ) : (
              <div style={{background:"var(--gray-50)",borderRadius:8,padding:"12px 14px"}}>
                <p style={{fontSize:13,color:"var(--gray-700)",marginBottom:10}}>
                  {lang==="en"
                    ? "You'll keep Pro access until the end of your current billing period. After that, you'll move to the Free plan automatically. Are you sure?"
                    : "Mantendrás el acceso Pro hasta el final de tu periodo de facturación actual. Después pasarás al plan Gratuito automáticamente. ¿Confirmas?"}
                </p>
                {cancelError && <div className="alert alert-error" style={{marginBottom:10,padding:"6px 10px",fontSize:12}}>{cancelError}</div>}
                <div style={{display:"flex",gap:8}}>
                  <button type="button" className="btn btn-outline btn-sm" onClick={handleCancelSubscription} disabled={canceling}>
                    {canceling ? "..." : (lang==="en"?"Yes, cancel":"Sí, cancelar")}
                  </button>
                  <button type="button" className="btn btn-primary btn-sm" onClick={()=>{setCancelConfirm(false);setCancelError("");}}>
                    {lang==="en"?"Keep Pro":"Mantener Pro"}
                  </button>
                </div>
              </div>
            )}
          </>
        )}
        {isPro && subStatus === "canceling" && (
          <p style={{fontSize:14,color:"var(--amber)"}}>
            {lang==="en"
              ? `Your Pro plan will end on ${subEndsAt ? new Date(subEndsAt).toLocaleDateString("en-GB") : "the end of your billing period"}. You'll keep full access until then.`
              : `Tu plan Pro terminará el ${subEndsAt ? new Date(subEndsAt).toLocaleDateString("es-ES") : "final de tu periodo de facturación"}. Mantendrás el acceso completo hasta entonces.`}
          </p>
        )}
      </div>

      <form onSubmit={handleSave} style={{maxWidth:560}}>

        {/* Idioma */}
        <div className="card" style={{marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:16}}>{T("settings_lang")}</h3>
          <div style={{display:"flex",gap:8}}>
            {[["es","Español"],["en","English"]].map(([code,label])=>(
              <button key={code} type="button" onClick={()=>updateLang(code)}
                style={{padding:"8px 18px",borderRadius:20,border:`2px solid ${lang===code?"var(--blue)":"var(--border)"}`,
                        background:lang===code?"var(--blue-l)":"var(--card)",color:lang===code?"var(--blue)":"var(--gray-600)",
                        fontSize:14,fontWeight:500,cursor:"pointer"}}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Moneda */}
        <div className="card" style={{marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:6,display:"flex",alignItems:"center",gap:6}}><Coins size={16}/> {lang==="en"?"Currency":"Moneda"}</h3>
          <p style={{fontSize:12,color:"var(--gray-500)",marginBottom:12}}>
            {lang==="en"?"Slide to see more currencies":"Deslízate para ver más monedas"}
          </p>
          <div style={{display:"flex",gap:8,overflowX:"auto",paddingBottom:6,WebkitOverflowScrolling:"touch"}}>
            {CURRENCIES.map(({code,label})=>(
              <button key={code} type="button" onClick={()=>updateCurrency(code)}
                style={{padding:"8px 18px",borderRadius:20,border:`2px solid ${currency===code?"var(--blue)":"var(--border)"}`,
                        background:currency===code?"var(--blue-l)":"var(--card)",color:currency===code?"var(--blue)":"var(--gray-600)",
                        fontSize:14,fontWeight:500,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Tema */}
        <div id="onboarding-theme" className="card" style={{marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6}}><Palette size={16}/> {lang==="en"?"Theme":"Tema"}</h3>
          <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(140px,1fr))",gap:8}}>
            {[
              ["light",   "Claro",    "#f8fafc", "#1e293b"],
              ["cian",    "Cian",     "#00b4d8", "#ffffff"],
              ["zafiro",  "Zafiro",   "#0a1628", "#2563eb"],
              ["arcoiris","Arcoíris", "#fff0f8", "#ec4899"],
              ["elite",   "Élite",    "#0a0a0a", "#d4af37"],
              ["neon",    "Neón",     "#0d0015", "#e91e8c"],
              ["menta",   "Menta",    "#f0fdf4", "#65a30d"],
              ["matrix",  "Matrix",   "#030a00", "#84cc16"],
              ["sakura",  "Sakura",   "#fff5f7", "#e91e8c"],
              ["luxe",    "Luxe",     "#faf8f3", "#c9a84c"],
              ["cafe",    "Café",     "#fbf4ec", "#b5652f"],
              ["king",    "King",     "#f5f3ef", "#d4af37"],
            ].map(([code, label, bg, accent]) => (
              <button key={code} type="button" onClick={() => updateTheme(code)}
                style={{
                  padding:"10px 8px", borderRadius:10, cursor:"pointer",
                  border: theme===code ? `2px solid ${accent}` : "2px solid var(--border)",
                  background: bg, display:"flex", flexDirection:"column",
                  alignItems:"center", gap:4,
                  boxShadow: theme===code ? `0 0 0 2px ${accent}40` : "none",
                  transition:"all .15s"
                }}>
                <span style={{width:18, height:18, borderRadius:"50%", background:accent, border:`2px solid ${bg}`, boxShadow:"0 0 0 1px rgba(0,0,0,.08)"}}/>
                <span style={{fontSize:12, fontWeight:600, color: accent}}>{label}</span>
                {theme===code && <span style={{fontSize:10, color: accent, display:"flex", alignItems:"center", gap:3}}><CircleCheck size={10}/> Activo</span>}
              </button>
            ))}
          </div>
        </div>

        {/* Seguridad — Face ID / huella. Solo se muestra si el propio
            dispositivo/navegador tiene sensor biométrico; en la mayoría
            de ordenadores no lo hay, así que ahí no aparece la tarjeta. */}
        {bioLock.supported && (
          <div className="card" style={{marginBottom:16}}>
            <h3 style={{fontSize:15,fontWeight:600,marginBottom:6,display:"flex",alignItems:"center",gap:6}}>
              <Fingerprint size={16}/> {lang==="en" ? "Security" : "Seguridad"}
            </h3>
            <label style={{display:"flex",alignItems:"center",gap:10,cursor:"pointer",marginTop:6}}>
              <input type="checkbox" checked={bioLock.enabled} disabled={bioBusy}
                     onChange={handleToggleBioLock}
                     style={{width:18,height:18,cursor:"pointer"}}/>
              <span style={{fontSize:14,fontWeight:500,color:"var(--gray-700)"}}>
                {lang==="en" ? "Unlock with Face ID / fingerprint" : "Desbloquear con Face ID / huella"}
              </span>
            </label>
            <p className="form-hint" style={{marginTop:8}}>
              {lang==="en"
                ? "Every time you open the app (or come back to it), you'll confirm with your device's Face ID/fingerprint instead of typing your password. This only applies to this device/browser — you'll need to turn it on again if you use another phone or clear your browser data."
                : "Cada vez que abras la app (o vuelvas a ella) confirmarás con el Face ID/huella de tu dispositivo en vez de escribir la contraseña. Esto solo aplica a este dispositivo/navegador — si usas otro móvil o borras los datos del navegador, tendrás que activarlo de nuevo ahí."}
            </p>
          </div>
        )}

        {/* Etiquetas */}
        <div className="card" style={{marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:16}}>{T("settings_labels")}</h3>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{T("settings_income_label")}</label>
              <input className="form-input" value={cfg.income_label} onChange={e=>setCfg({...cfg,income_label:e.target.value})}/></div>
            <div className="form-group"><label className="form-label">{T("settings_expense_label")}</label>
              <input className="form-input" value={cfg.expense_label} onChange={e=>setCfg({...cfg,expense_label:e.target.value})}/></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label className="form-label">{T("settings_balance_label")}</label>
              <input className="form-input" value={cfg.balance_label} onChange={e=>setCfg({...cfg,balance_label:e.target.value})}/></div>
            <div className="form-group"><label className="form-label">{T("settings_fund_label")}</label>
              <input className="form-input" value={cfg.fund_label} onChange={e=>setCfg({...cfg,fund_label:e.target.value})}/></div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{lang==="en"?"Budgets label":"Nombre de presupuestos"}</label>
              <input className="form-input" value={cfg.budget_label} onChange={e=>setCfg({...cfg,budget_label:e.target.value})}
                     placeholder={lang==="en"?"e.g. Goals, Budgets...":"Ej: Metas, Presupuestos..."}/>
            </div>
          </div>
          <div className="form-group" style={{maxWidth:200}}>
            <label className="form-label">{T("settings_fund_pct")}</label>
            <input className="form-input" type="number" min="0" max="100" step="0.5"
                   value={cfg.fund_pct} onChange={e=>setCfg({...cfg,fund_pct:e.target.value})}/>
          </div>
        </div>

        {/* Panel personalizable */}
        <div className="card" style={{marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6}}><Monitor size={16}/> {lang==="en"?"Panel sections":"Secciones del panel"}</h3>
          <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:12}}>
            {lang==="en"?"Choose what to show on your main panel":"Elige qué mostrar en tu panel principal"}
          </p>
          {[
            ["grafico",      lang==="en"?"Income/expense chart":"Gráfico ingresos/gastos", BarChart3],
            ["balance",      lang==="en"?"Balance metrics":"Métricas de balance", Wallet],
            ["presupuestos", lang==="en"?"Budgets":"Presupuestos", Target],
            ["huchas",       lang==="en"?"Savings jars":"Huchas", PiggyBank],
            ["cuentas",      lang==="en"?"Accounts":"Cuentas", CreditCard],
            ["historial",    lang==="en"?"Monthly history":"Historial mensual", Calendar],
          ].map(([key, label, Icon]) => (
            <div key={key} style={{display:"flex",justifyContent:"space-between",alignItems:"center",
                                   padding:"10px 0",borderBottom:"1px solid var(--border)"}}>
              <span style={{fontSize:14,color:"var(--gray-700)",display:"flex",alignItems:"center",gap:7}}><Icon size={15}/> {label}</span>
              <button type="button"
                onClick={() => setCfg(prev => ({
                  ...prev,
                  panel_sections: { ...prev.panel_sections, [key]: !prev.panel_sections[key] }
                }))}
                style={{
                  width:44, height:24, borderRadius:12, border:"none", cursor:"pointer",
                  background: cfg.panel_sections[key] ? "var(--green)" : "var(--gray-200)",
                  position:"relative", transition:"background .2s"
                }}>
                <div style={{
                  width:18, height:18, borderRadius:"50%", background:"white",
                  position:"absolute", top:3,
                  left: cfg.panel_sections[key] ? 23 : 3,
                  transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,.2)"
                }} />
              </button>
            </div>
          ))}
        </div>

        {/* Campos personalizados (solo modo negocio) */}
        {profile?.perfil === "negocio" && (
          <div className="card" style={{marginBottom:16}}>
            <h3 style={{fontSize:15,fontWeight:600,marginBottom:16,display:"flex",alignItems:"center",gap:6}}>
              <Receipt size={16}/> {lang==="en"?"Custom fields":"Campos personalizados"}
            </h3>
            <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:12}}>
              {lang==="en"
                ? "Add up to 10 extra fields to track on each entry (e.g. Client, Order #, Table)."
                : "Añade hasta 10 campos propios para guardar información extra en cada entrada (ej: Cliente, Nº pedido, Mesa)."}
            </p>
            {cfg.custom_fields.map((field, i) => (
              <div key={i} style={{display:"flex",alignItems:"center",gap:10,
                                   padding:"10px 0",
                                   borderBottom: i < cfg.custom_fields.length-1 ? "1px solid var(--border)" : "none"}}>
                <input className="form-input" style={{flex:1}}
                       value={field.name}
                       placeholder={lang==="en" ? `Field ${i+1} name` : `Nombre del campo ${i+1}`}
                       onChange={e => setCfg(prev => {
                         const next = [...prev.custom_fields];
                         next[i] = { ...next[i], name: e.target.value };
                         return { ...prev, custom_fields: next };
                       })}/>
                <button type="button"
                  onClick={() => setCfg(prev => {
                    const next = [...prev.custom_fields];
                    next[i] = { ...next[i], enabled: !next[i].enabled };
                    return { ...prev, custom_fields: next };
                  })}
                  style={{
                    width:44, height:24, borderRadius:12, border:"none", cursor:"pointer",
                    background: field.enabled ? "var(--green)" : "var(--gray-200)",
                    position:"relative", transition:"background .2s", flexShrink:0
                  }}>
                  <div style={{
                    width:18, height:18, borderRadius:"50%", background:"white",
                    position:"absolute", top:3,
                    left: field.enabled ? 23 : 3,
                    transition:"left .2s", boxShadow:"0 1px 3px rgba(0,0,0,.2)"
                  }} />
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Categorías */}
        <div id="onboarding-cats" className="card" style={{marginBottom:16}}>
          <h3 style={{fontSize:15,fontWeight:600,marginBottom:16}}>{T("settings_cats")}</h3>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">{T("settings_income_cats")} {cfg.income_label} {T("settings_cats_hint")}</label>
              <textarea className="form-input" rows={6} value={cfg.income_cats}
                        onChange={e=>setCfg({...cfg,income_cats:e.target.value})}
                        style={{resize:"vertical",fontFamily:"var(--font)"}}/>
            </div>
            <div className="form-group">
              <label className="form-label">{T("settings_expense_cats")} {cfg.expense_label} {T("settings_cats_hint")}</label>
              <textarea className="form-input" rows={6} value={cfg.expense_cats}
                        onChange={e=>setCfg({...cfg,expense_cats:e.target.value})}
                        style={{resize:"vertical",fontFamily:"var(--font)"}}/>
            </div>
          </div>
        </div>

        <div style={{display:"flex",gap:10}}>
          <button className="btn btn-primary" type="submit">{T("settings_save")}</button>
          <button className="btn btn-outline" type="button" onClick={()=>{if(window.confirm(T("settings_logout_confirm")))logout().then(()=>navigate("/"));}}>
            {T("settings_logout")}
          </button>
        </div>

        <DataManagement lang={lang} />

      </form>

      {/* Legal y contacto — visible siempre (en la app Android el footer de
          escritorio con estos enlaces no se ve, así que se repite aquí). */}
      <div className="card" style={{marginTop:16}}>
        <h3 style={{fontSize:15,fontWeight:600,marginBottom:14}}>
          {lang==="en" ? "Legal & support" : "Legal y soporte"}
        </h3>
        <div style={{display:"flex",flexDirection:"column",gap:8}}>
          <a href="/legal?tab=terminos" className="btn btn-outline" style={{justifyContent:"flex-start",display:"flex",alignItems:"center",gap:6}}>
            <FileText size={15}/> {lang==="en" ? "Terms & conditions" : "Términos y condiciones"}
          </a>
          <a href="/legal?tab=privacidad" className="btn btn-outline" style={{justifyContent:"flex-start",display:"flex",alignItems:"center",gap:6}}>
            <Lock size={15}/> {lang==="en" ? "Privacy policy" : "Política de privacidad"}
          </a>
          <a href="mailto:aariapc@gmail.com?subject=ARIA%20Wallet%20-%20Contacto"
             className="btn btn-outline" style={{justifyContent:"flex-start",display:"flex",alignItems:"center",gap:6}}>
            <Mail size={15}/> {lang==="en" ? "Contact us" : "Contactar"}
          </a>
        </div>
        <p style={{fontSize:12,color:"var(--gray-400)",marginTop:10}}>
          {lang==="en" ? "aariapc@gmail.com" : "aariapc@gmail.com · AriaPC, Lleida"}
        </p>
      </div>

    </div>
  );
}

// ── Gestión de datos (exportar + borrar) ─────────────────────────────────────
function DataManagement({ lang }) {
  const { user, profile, logout } = useAuth();
  const { entries } = useEntries();
  const c = profile?.config || {};
  const isNegocio = profile?.perfil === "negocio";
  const activeCustomFields = isNegocio
    ? (c.custom_fields || [])
        .map((f, i) => ({ ...f, key: `custom${i + 1}` }))
        .filter(f => f.enabled && f.name && f.name.trim())
    : [];
  const navigate = useNavigate();
  const [delStep, setDelStep] = useState(0);
  const [delProgress, setDelProgress] = useState(0);

  // Borrado de cuenta definitivo
  // accStep: 0 inicial, 1 confirmación, 2 pide contraseña, 3 borrando, 4 hecho
  const [accStep, setAccStep] = useState(0);
  const [accPassword, setAccPassword] = useState("");
  const [accError, setAccError] = useState("");
  const [accLoading, setAccLoading] = useState(false);

  const MONTHS_L = ["Enero","Febrero","Marzo","Abril","Mayo","Junio",
                    "Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"];

  // Selector de periodo para exportar: por defecto "Todos" (comportamiento
  // de siempre), pero se puede elegir un año y/o mes concreto para exportar
  // solo ese periodo, ya que mucha gente guarda sus datos mes a mes.
  const [exportYear,  setExportYear]  = useState("Todos");
  const [exportMonth, setExportMonth] = useState("Todos");
  const exportYears = [...new Set(entries.map(e=>e.year))].sort();

  async function exportAllExcel() {
    const toExport = entries.filter(e =>
      (exportYear==="Todos"  || e.year===parseInt(exportYear)) &&
      (exportMonth==="Todos" || e.month===exportMonth)
    );
    if (toExport.length === 0) {
      alert(lang==="en"?"No entries to export for that period.":"No hay entradas para exportar en ese periodo.");
      return;
    }
    const wb = XLSX.utils.book_new();

    if (isNegocio) {
      // Modo negocio: una hoja por mes, una fila por operación, orden
      // cronológico, balance acumulado y columnas propias por cada campo
      // personalizado (Cliente, Matrícula, Mesa...).
      const byMonth = {};
      toExport.forEach(e => {
        const key = `${e.year}-${e.month}`;
        if (!byMonth[key]) byMonth[key] = { year: e.year, month: e.month, entries: [] };
        byMonth[key].entries.push(e);
      });
      const sortedKeys = Object.keys(byMonth).sort((a,b) => {
        const [ay,am] = a.split("-"); const [by2,bm] = b.split("-");
        return ay!==by2 ? ay-by2 : MONTHS_L.indexOf(am)-MONTHS_L.indexOf(bm);
      });
      sortedKeys.forEach(key => {
        const { year, month, entries: monthEntries } = byMonth[key];
        const { wsData, colCount, catCol, descCol, incomeCol, expenseCol, profitCol } = buildBusinessSheetData(monthEntries, activeCustomFields, lang);
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws["!cols"] = Array(colCount).fill({wch:18});
        styleBusinessSheet(ws, { catCol, descCol, incomeCol, expenseCol, profitCol });
        XLSX.utils.book_append_sheet(wb, ws, `${month}_${year}`.slice(0,31));
      });
    } else {
      // Modo personal: formato clásico COMPRAS/VENTAS en columnas paralelas
      const byMonth = {};
      toExport.forEach(e => {
        const key = `${e.year}-${e.month}`;
        if (!byMonth[key]) byMonth[key] = { year: e.year, month: e.month, income: [], expense: [] };
        if (e.type==="income")  byMonth[key].income.push(e);
        if (e.type==="expense") byMonth[key].expense.push(e);
      });
      const sortedKeys = Object.keys(byMonth).sort((a,b) => {
        const [ay,am] = a.split("-"); const [by2,bm] = b.split("-");
        return ay!==by2 ? ay-by2 : MONTHS_L.indexOf(am)-MONTHS_L.indexOf(bm);
      });
      sortedKeys.forEach(key => {
        const { year, month, income, expense } = byMonth[key];
        const maxRows = Math.max(income.length, expense.length);
        const wsData = [
          ["FECHA", "COMPRAS", "VALOR", "", "VENTAS", "VALOR", "BENEFICIO"],
        ];
        let totalIncome = 0;
        let totalExpense = 0;
        for (let i = 0; i < maxRows; i++) {
          const inc = income[i];
          const exp = expense[i];
          const fechaInc = inc?.datetime ? inc.datetime.slice(0,10).split("/").reverse().join("-") : `${year}-${String(MONTHS_L.indexOf(month)+1).padStart(2,"0")}-01`;
          const fechaExp = exp?.datetime ? exp.datetime.slice(0,10).split("/").reverse().join("-") : `${year}-${String(MONTHS_L.indexOf(month)+1).padStart(2,"0")}-01`;
          const fecha = exp ? fechaExp : fechaInc;
          if (inc) totalIncome  += Number(inc.amount) || 0;
          if (exp) totalExpense += Number(exp.amount) || 0;
          wsData.push([
            fecha,
            exp ? (exp.desc || exp.cat || "") : "",
            exp ? exp.amount : "",
            "",
            inc ? (inc.desc || inc.cat || "") : "",
            inc ? inc.amount : "",
            "",
          ]);
        }
        wsData.push(["","","","","","",""]);
        wsData.push(["TOTALES", "Total gastos", totalExpense.toFixed(2), "", "Total ingresos", totalIncome.toFixed(2), (totalIncome - totalExpense).toFixed(2)]);
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        ws["!cols"] = [{wch:14},{wch:28},{wch:8},{wch:4},{wch:28},{wch:8},{wch:10}];
        stylePersonalSheet(ws);
        const sheetName = `${month}_${year}`.slice(0,31);
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
    }

    const periodo = (exportMonth!=="Todos" || exportYear!=="Todos")
      ? `${exportMonth!=="Todos"?exportMonth+"_":""}${exportYear!=="Todos"?exportYear:""}`
      : new Date().toISOString().slice(0,10);
    XLSX.writeFile(wb, `mis_datos_${periodo}.xlsx`);
  }

  async function deleteAllData() {
    if (!user) return;
    setDelStep(2); setDelProgress(0);
    try {
      const ref = collection(db, "users", user.uid, "entries");
      const snap = await getDocs(ref);
      const docs = snap.docs;
      // Si alguna de estas entradas tenía una foto/documento guardado
      // (receiptPath), se borra también de Storage antes de borrar la
      // entrada — si no, el archivo se quedaría huérfano ahí para
      // siempre, sin que nadie pueda volver a verlo ni borrarlo.
      const { deleteAttachment } = await import("./receiptOcr");
      for (let i = 0; i < docs.length; i++) {
        const data = docs[i].data();
        if (data.receiptPath) await deleteAttachment(data.receiptPath);
        await deleteDoc(doc(db, "users", user.uid, "entries", docs[i].id));
        setDelProgress(Math.round(((i+1)/docs.length)*100));
        if (i % 10 === 9) await new Promise(r => setTimeout(r, 100));
      }
      setDelStep(3);
    } catch (err) {
      alert(lang==="en"?"Error: "+err.message:"Error: "+err.message);
      setDelStep(0);
    }
  }

  // Borra la cuenta de forma definitiva e irreversible:
  // 1) reautentica con contraseña (Firebase exige sesión reciente para esto),
  // 2) llama a la Cloud Function "deleteAccount" que cancela Stripe si aplica
  //    y borra entries/pendientes/perfil en Firestore,
  // 3) borra el usuario de Firebase Auth (lo hace la función con admin SDK),
  // 4) cierra sesión local y redirige a la landing.
  async function confirmDeleteAccount() {
    setAccError("");
    if (!accPassword) {
      setAccError(lang==="en" ? "Enter your password" : "Introduce tu contraseña");
      return;
    }
    setAccLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, accPassword);
      await reauthenticateWithCredential(user, credential);

      // Se borran TODOS los archivos guardados del usuario (fotos de
      // tickets, adjuntos) antes de llamar a la función que borra la
      // cuenta. Tiene que hacerse aquí y no después: las reglas de
      // Storage exigen una sesión autenticada con ese mismo uid, y una
      // vez la Cloud Function borre el usuario esa sesión ya no vale.
      // deleteAllAttachments nunca lanza error, así que si algo falla
      // (red, permisos...) no bloquea el resto del borrado de la cuenta.
      const { deleteAllAttachments } = await import("./receiptOcr");
      await deleteAllAttachments(user.uid);

      setAccStep(3);
      const functions = getFunctions();
      const deleteAccountFn = httpsCallable(functions, "deleteAccount");
      await deleteAccountFn();

      setAccStep(4);
      setTimeout(async () => {
        try { await logout(); } catch (e) { /* la cuenta ya no existe, no pasa nada */ }
        navigate("/");
      }, 2000);
    } catch (err) {
      setAccLoading(false);
      setAccStep(2);
      if (err.code === "auth/wrong-password" || err.code === "auth/invalid-credential") {
        setAccError(lang==="en" ? "Incorrect password" : "Contraseña incorrecta");
      } else {
        setAccError(lang==="en" ? "Error: " + err.message : "Error: " + err.message);
      }
    }
  }

  return (
    <div style={{marginTop:24,borderTop:"1px solid var(--border)",paddingTop:20}}>
      <h3 style={{fontSize:15,fontWeight:700,color:"var(--gray-800)",marginBottom:4,display:"flex",alignItems:"center",gap:6}}>
        <Archive size={16}/> {lang==="en"?"My data":"Mis datos"}
      </h3>
      <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:16}}>
        {lang==="en"
          ? "Export all your entries to Excel or permanently delete them from the database."
          : "Exporta todas tus entradas a Excel o elimínalas permanentemente de la base de datos."}
      </p>

      <div style={{display:"flex",gap:8,flexWrap:"wrap",marginBottom:10}}>
        <select className="filter-select" value={exportYear} onChange={e=>setExportYear(e.target.value)}>
          <option value="Todos">{lang==="en"?"All years":"Todos los años"}</option>
          {exportYears.map(y=><option key={y} value={y}>{y}</option>)}
        </select>
        <select className="filter-select" value={exportMonth} onChange={e=>setExportMonth(e.target.value)}>
          <option value="Todos">{lang==="en"?"All months":"Todos los meses"}</option>
          {MONTHS_L.map(m=><option key={m} value={m}>{m}</option>)}
        </select>
      </div>

      <div style={{display:"flex",gap:10,flexWrap:"wrap",marginBottom:16}}>
        <button type="button" onClick={exportAllExcel} className="btn btn-outline"
          style={{display:"flex",alignItems:"center",gap:6}}>
          <FileDown size={15}/> {(exportMonth!=="Todos"||exportYear!=="Todos")
                ? (lang==="en"?"Export selected period":"Exportar periodo seleccionado")
                : (lang==="en"?"Export all to Excel":"Exportar todo a Excel")}
          <span style={{fontSize:11,color:"var(--gray-400)"}}>
            ({entries.filter(e=>(exportYear==="Todos"||e.year===parseInt(exportYear))&&(exportMonth==="Todos"||e.month===exportMonth)).length} {lang==="en"?"entries":"entradas"})
          </span>
        </button>
      </div>

      {delStep === 0 && (
        <button type="button" onClick={()=>setDelStep(1)} className="btn btn-danger btn-sm">
          <Trash2 size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Delete all my data":"Eliminar todos mis datos"}
        </button>
      )}

      {delStep === 1 && (
        <div className="card" style={{background:"var(--red-l)",border:"1px solid var(--red)",padding:"16px 20px"}}>
          <p style={{fontSize:14,fontWeight:700,color:"var(--red)",marginBottom:6}}>
            <AlertTriangle size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Are you sure?":"¿Estás seguro?"}
          </p>
          <p style={{fontSize:13,color:"var(--red)",marginBottom:8}}>
            {lang==="en"
              ? `This will permanently delete all ${entries.length} entries. This cannot be undone.`
              : `Esto eliminará permanentemente las ${entries.length} entradas. Esta acción no se puede deshacer.`}
          </p>
          <p style={{fontSize:12,color:"var(--red)",marginBottom:14,fontWeight:600}}>
            {lang==="en"?"Export to Excel first to keep a copy.":"Exporta a Excel primero para guardar una copia."}
          </p>
          <div style={{display:"flex",gap:8}}>
            <button type="button" onClick={deleteAllData}
              style={{background:"var(--red)",color:"white",border:"none",padding:"8px 16px",
                      borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13}}>
              {lang==="en"?"Yes, delete everything":"Sí, eliminar todo"}
            </button>
            <button type="button" onClick={()=>setDelStep(0)} className="btn btn-outline btn-sm">
              {lang==="en"?"Cancel":"Cancelar"}
            </button>
          </div>
        </div>
      )}

      {delStep === 2 && (
        <div className="card" style={{padding:"16px 20px"}}>
          <p style={{fontSize:13,fontWeight:600,color:"var(--gray-700)",marginBottom:10}}>
            <Trash2 size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang==="en"?"Deleting...":"Eliminando..."} {delProgress}%
          </p>
          <div className="limit-bar" style={{height:8}}>
            <div className="limit-fill full" style={{width:`${delProgress}%`}}/>
          </div>
        </div>
      )}

      {delStep === 3 && (
        <div className="alert alert-success">
          <CircleCheck size={14} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"All data deleted successfully.":"Todos los datos eliminados correctamente."}
        </div>
      )}

      {/* ── Eliminar cuenta definitivamente ── */}
      <div style={{marginTop:28,borderTop:"1px solid var(--border)",paddingTop:20}}>
        <h3 style={{fontSize:15,fontWeight:700,color:"var(--red)",marginBottom:4}}>
          <AlertTriangle size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Delete account":"Eliminar cuenta"}
        </h3>
        <p style={{fontSize:13,color:"var(--gray-500)",marginBottom:16}}>
          {lang==="en"
            ? "Permanently delete your account, all your data, and cancel any active subscription. This cannot be undone."
            : "Elimina tu cuenta de forma permanente, todos tus datos, y cancela cualquier suscripción activa. Esta acción no se puede deshacer."}
        </p>

        {accStep === 0 && (
          <button type="button" onClick={()=>setAccStep(1)} className="btn btn-danger btn-sm">
            <Trash2 size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Delete my account":"Eliminar mi cuenta"}
          </button>
        )}

        {accStep === 1 && (
          <div className="card" style={{background:"var(--red-l)",border:"1px solid var(--red)",padding:"16px 20px"}}>
            <p style={{fontSize:14,fontWeight:700,color:"var(--red)",marginBottom:6}}>
              <AlertTriangle size={15} style={{display:"inline",verticalAlign:"-3px",marginRight:5}}/> {lang==="en"?"Are you absolutely sure?":"¿Estás completamente seguro?"}
            </p>
            <p style={{fontSize:13,color:"var(--red)",marginBottom:14}}>
              {lang==="en"
                ? "Your account, all entries, pending payments, settings, and any active subscription will be permanently deleted. You will not be able to recover this."
                : "Tu cuenta, todas las entradas, pendientes, ajustes y cualquier suscripción activa se eliminarán de forma permanente. No podrás recuperar esto."}
            </p>
            <div style={{display:"flex",gap:8}}>
              <button type="button" onClick={()=>setAccStep(2)}
                style={{background:"var(--red)",color:"white",border:"none",padding:"8px 16px",
                        borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13}}>
                {lang==="en"?"Continue":"Continuar"}
              </button>
              <button type="button" onClick={()=>setAccStep(0)} className="btn btn-outline btn-sm">
                {lang==="en"?"Cancel":"Cancelar"}
              </button>
            </div>
          </div>
        )}

        {accStep === 2 && (
          <div className="card" style={{background:"var(--red-l)",border:"1px solid var(--red)",padding:"16px 20px"}}>
            <p style={{fontSize:13,fontWeight:600,color:"var(--red)",marginBottom:10,display:"flex",alignItems:"center",gap:6}}>
              <Lock size={14}/> {lang==="en"
                ? "Enter your password to confirm account deletion"
                : "Introduce tu contraseña para confirmar la eliminación"}
            </p>
            {accError && <div className="alert alert-error" style={{marginBottom:10,padding:"8px 10px",fontSize:12}}>{accError}</div>}
            <input className="form-input" type="password" value={accPassword}
                   onChange={e=>setAccPassword(e.target.value)}
                   placeholder={lang==="en"?"Your password":"Tu contraseña"}
                   style={{marginBottom:12}} autoFocus/>
            <div style={{display:"flex",gap:8}}>
              <button type="button" onClick={confirmDeleteAccount} disabled={accLoading}
                style={{background:"var(--red)",color:"white",border:"none",padding:"8px 16px",
                        borderRadius:8,cursor:"pointer",fontWeight:700,fontSize:13}}>
                {accLoading ? "..." : (lang==="en"?"Yes, delete my account":"Sí, eliminar mi cuenta")}
              </button>
              <button type="button" onClick={()=>{setAccStep(0);setAccPassword("");setAccError("");}} className="btn btn-outline btn-sm">
                {lang==="en"?"Cancel":"Cancelar"}
              </button>
            </div>
          </div>
        )}

        {accStep === 3 && (
          <div className="card" style={{padding:"16px 20px"}}>
            <p style={{fontSize:13,fontWeight:600,color:"var(--gray-700)"}}>
              <Trash2 size={14} style={{display:"inline",verticalAlign:"-2px",marginRight:4}}/> {lang==="en"?"Deleting your account...":"Eliminando tu cuenta..."}
            </p>
          </div>
        )}

        {accStep === 4 && (
          <div className="alert alert-success" style={{display:"flex",alignItems:"center",gap:6}}>
            <CircleCheck size={15}/> {lang==="en"
              ? "Your account has been permanently deleted. Redirecting..."
              : "Tu cuenta ha sido eliminada de forma permanente. Redirigiendo..."}
          </div>
        )}
      </div>
    </div>
  );
}
export function SuccessPage() {
  const navigate = useNavigate();
  const { lang } = useAuth();
  const T = (key) => t(lang, key);
  return (
    <div className="page">
      <div style={{maxWidth:480,margin:"0 auto",textAlign:"center",padding:"60px 24px"}}>
        <div style={{marginBottom:16,display:"flex",justifyContent:"center"}}><PartyPopper size={56}/></div>
        <h2 style={{fontSize:26,fontWeight:800,color:"var(--gray-900)",marginBottom:8}}>
          {T("success_title")}
        </h2>
        <p style={{fontSize:16,color:"var(--gray-500)",marginBottom:32}}>
          {T("success_sub")}
        </p>
        <button className="btn btn-primary" style={{width:"100%",justifyContent:"center",padding:"14px",fontSize:16}}
          onClick={()=>navigate("/panel")}>
          {T("success_btn")}
        </button>
      </div>
    </div>
  );
}